import { useState, useEffect } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useUser } from "contexts/UserContext";
import queryBusinessData from "services/queryBusinessData";
import SessionsOutlook from "../../../../components/sessions-outlook";
import { ReactComponent as BlueBullet32 } from "assets/svg/blue-bullet-32.svg";
import { ReactComponent as LightBlueBullet32 } from "assets/svg/light-blue-bullet-32.svg";
import { ReactComponent as OrangeBullet32 } from "assets/svg/orange-bullet-32.svg";

import {
  PieChart,
  Pie,
  Legend,
  Tooltip as RechartsTooltip,
  Cell,
  ResponsiveContainer,
  Sector,
} from "recharts";
import styles from "./styles.module.scss";
import { RADIAN, COLORS } from "./constants";

const year = new Date().getFullYear();

const DataBreakdown = () => {
  const { user } = useUser();
  const [, setError] = useState("");
  const [data, setData] = useState([] as any);

  // get business analytics data on init
  useEffect(() => {
    if (!user) {
      return;
    }
    queryBusinessData(year)
      .then((res) => {
        const { sessions_completed, upcoming_sessions, cancelled_sessions } =
          res;
        setData([
          {
            name: "Completed Sessions",
            value: sessions_completed,
          },
          {
            name: "Upcoming Sessions",
            value: upcoming_sessions,
          },
          {
            name: "Cancelled Sessions",
            value: cancelled_sessions,
          },
        ]);
      })
      .catch((err) => {
        setError(err);
      });
  }, [user]);

  const renderLegend = (props) => {
    const { payload } = props;

    return data.reduce((prev, curr) => {
      return prev + curr["value"];
    }, 0) === 0 ? (
      <div>
        <span className={styles["pie-chart__legend"]}>
          <BlueBullet32 />
          <div>
            <div className={styles["pie-chart__legend-text"]}>
              Completed Sessions
            </div>
            <div className={styles["pie-chart__legend-num"]}>0</div>
          </div>
        </span>
        <span className={styles["pie-chart__legend"]}>
          <LightBlueBullet32 />
          <div>
            <div className={styles["pie-chart__legend-text"]}>
              Upcoming Sessions
            </div>
            <div className={styles["pie-chart__legend-num"]}>0</div>
          </div>
        </span>
        <span className={styles["pie-chart__legend"]}>
          <OrangeBullet32 />
          <div>
            <div className={styles["pie-chart__legend-text"]}>
              Cancelled Sessions
            </div>
            <div className={styles["pie-chart__legend-num"]}>0</div>
          </div>
        </span>
      </div>
    ) : (
      <div>
        {payload.map((entry, index) => (
          <span className={styles["pie-chart__legend"]} key={`item-${index}`}>
            {entry.value === "Completed Sessions" ? (
              <BlueBullet32 />
            ) : entry.value === "Upcoming Sessions" ? (
              <LightBlueBullet32 />
            ) : (
              <OrangeBullet32 />
            )}
            <div>
              <div className={styles["pie-chart__legend-text"]}>
                {entry.value}
              </div>
              <div className={styles["pie-chart__legend-num"]}>
                {entry.payload?.value}
              </div>
            </div>
          </span>
        ))}
      </div>
    );
  };

  const [pieActiveIndex, setPieActiveIndex] = useState(-1);

  const renderActiveShape = ({
    cx,
    cy,
    startAngle,
    endAngle,
    fill,
    stroke,
    strokeWidth,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    value,
  }) => {
    const radius = outerRadius / 2 + innerRadius / 2;
    // the 2.5 is the needed offset generated by lib
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return !percent ? (
      <g></g>
    ) : (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          stroke={stroke}
          strokeWidth={strokeWidth}
        />
        <g textAnchor={"middle"} dominantBaseline={"central"}>
          <OverlayTrigger
            show={true}
            placement="top"
            overlay={
              <Tooltip
                id="pie-chart-tooltip"
                className={styles["pie-chart__tooltip"]}
              >
                {value}
              </Tooltip>
            }
          >
            <circle cx={x} cy={y} r={0} />
          </OverlayTrigger>
        </g>
      </g>
    );
  };

  return (
    <>
      <SessionsOutlook isInDashboardHome={false} />
      <div className={styles["pie-chart"]}>
        <div className={styles["pie-chart__header"]}>
          <div className={styles["pie-chart__title"]}>
            Total sessions booked in {year}
          </div>
          <div className={styles["pie-chart__sub-title-num"]}>
            {data.reduce((prev, curr) => {
              return prev + curr["value"];
            }, 0)}
          </div>
        </div>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            {data.reduce((prev, curr) => {
              return prev + curr["value"];
            }, 0) === 0 ? (
              <Pie
                data={[{ name: "No Data", value: 1 }]}
                innerRadius={80}
                outerRadius={134}
                fill="#E7E7E7"
                dataKey="value"
                stroke={"none"}
              />
            ) : (
              <>
                <Pie
                  onMouseEnter={(_, index) => setPieActiveIndex(() => index)}
                  onMouseLeave={() => setPieActiveIndex(() => -1)}
                  data={data}
                  innerRadius={80}
                  outerRadius={134}
                  paddingAngle={0}
                  dataKey="value"
                  labelLine={false}
                  // for tooltip
                  activeShape={renderActiveShape as any}
                  activeIndex={pieActiveIndex}
                  stroke={"none"}
                  strokeWidth={0}
                >
                  {data.map((_, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
              </>
            )}
            <RechartsTooltip content={() => null} />
            <Legend
              layout="vertical"
              verticalAlign="middle"
              wrapperStyle={{
                top: "50%",
                left: "70%",
                transform: "translate(0, -50%)",
              }}
              content={renderLegend}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </>
  );
};

export default DataBreakdown;
